import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageLoader } from "@/components/Kit";
import * as Icons from "lucide-react";
import { Lock, MapPin, Inbox, ArrowRight } from "lucide-react";

function toPascal(s) { return (s || "").split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(""); }
const ROUTE = { assembly_bay: "/app/assembly", charging_dock: "/app/charging", central_core: "/app/arcade", arena: "/app/arena", neon_district: "/app/shop", scrap_yard: "/app/scrapyard" };

export default function Station() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [notif, setNotif] = useState(null);
  useEffect(() => {
    api.get("/station").then((r) => setData(r.data));
    api.get("/trades/notifications").then((r) => setNotif(r.data)).catch(() => {});
  }, []);
  if (!data) return <PageLoader label="Mapping the station" />;

  const go = (room) => {
    if (room.locked) return;
    navigate(ROUTE[room.id] || `/app/room/${room.id}`);
  };

  return (
    <div data-testid="station-page">
      <span className="label-cap text-primary flex items-center gap-2"><MapPin className="w-4 h-4" /> Orbit Space Station</span>
      <h1 className="text-4xl sm:text-5xl font-black tracking-tighter mt-2 mb-1 font-head">Station Hallway</h1>
      <p className="text-muted-foreground mb-6">Clearance: <span className="text-primary font-bold mono">SCL-{data.scl}</span>. Tap a wing on the map to travel; shop kiosks open their catalog directly.</p>

      {/* Interactive Virtupets-style room map (SVG poly/circle area map, fully responsive) */}
      <div className="panel-glow p-2 mb-8 relative" data-testid="station-map">
        <img src="/assets/sprites/station_map.png" alt="Orbit station map" className="w-full rounded-[var(--radius)] block" />
        <svg viewBox="0 0 1264 848" className="absolute inset-2 w-[calc(100%-1rem)] h-[calc(100%-1rem)]" style={{ cursor: "pointer" }}>
          <style>{`.hot{fill:rgba(255,179,0,0.06);stroke:rgba(255,179,0,0.55);stroke-width:3;transition:fill .15s}
            .hot:hover{fill:rgba(255,179,0,0.26)} .node{fill:rgba(77,208,255,0.1);stroke:rgba(77,208,255,0.7);stroke-width:3;transition:fill .15s}
            .node:hover{fill:rgba(77,208,255,0.3)} .hl{fill:#fff;font:700 30px sans-serif;text-anchor:middle;pointer-events:none;text-shadow:0 2px 6px #000}`}</style>
          {[
            ["arcade", "Central Core", "70,70 430,70 430,330 70,330", 250, 205, "/app/arcade"],
            ["arena", "Battle Arena", "830,60 1200,60 1200,350 830,350", 1015, 210, "/app/arena"],
            ["training", "Training Bay", "70,520 440,520 440,790 70,790", 255, 660, "/app/training"],
            ["customize", "Fabrication", "830,520 1200,520 1200,790 830,790", 1015, 660, "/app/depot"],
          ].map(([id, label, pts, lx, ly, to]) => (
            <g key={id} data-testid={`map-room-${id}`} onClick={() => navigate(to)}>
              <polygon className="hot" points={pts} />
              <text className="hl" x={lx} y={ly}>{label}</text>
            </g>
          ))}
          {[
            ["trading", "Trading Post", 632, 150, "/app/trade"],
            ["bazaar", "Parts Bazaar", 632, 720, "/app/shop"],
          ].map(([id, label, cx, cy, to]) => (
            <g key={id} data-testid={`map-node-${id}`} onClick={() => navigate(to)}>
              <circle className="node" cx={cx} cy={cy} r={66} />
              <text className="hl" style={{ fontSize: 22 }} x={cx} y={cy + 8}>{label}</text>
            </g>
          ))}
        </svg>
      </div>

      {notif?.pending_offers > 0 && (
        <button data-testid="trade-offers-ping" onClick={() => navigate("/app/trade")}
          className="w-full mb-8 panel-glow p-4 flex items-center gap-4 text-left hud-corner group hover:-translate-y-0.5 transition-transform">
          <span className="relative w-11 h-11 rounded-[var(--radius)] grid place-items-center bg-accent/15 text-accent shrink-0">
            <Inbox className="w-5 h-5" />
            <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-accent animate-ping" />
            <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-accent" />
          </span>
          <div className="flex-1">
            <div className="font-head font-bold text-accent">You have {notif.pending_offers} new trade offer{notif.pending_offers > 1 ? "s" : ""}</div>
            <p className="text-sm text-muted-foreground">Pilots want to swap gear across {notif.open_trades} of your listing{notif.open_trades > 1 ? "s" : ""}. Head to the Trading Post to close the deal.</p>
          </div>
          <ArrowRight className="w-5 h-5 text-accent group-hover:translate-x-1 transition-transform" />
        </button>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {data.rooms.map((room) => {
          const Icon = Icons[toPascal(room.icon)] || MapPin;
          return (
            <button key={room.id} data-testid={`door-${room.id}`} onClick={() => go(room)}
              className={`panel p-5 text-left relative overflow-hidden transition-transform ${room.locked ? "opacity-70 cursor-not-allowed" : "hover:-translate-y-1"}`}>
              <div className="flex items-start justify-between mb-3">
                <span className="w-11 h-11 rounded-[var(--radius)] grid place-items-center" style={{ background: room.locked ? "hsl(var(--muted))" : "hsl(var(--primary)/0.15)", color: room.locked ? "hsl(var(--muted-foreground))" : "hsl(var(--primary))" }}>
                  {room.locked ? <Lock className="w-5 h-5" /> : <Icon className="w-6 h-6" />}
                </span>
                {room.status === "soon" && !room.locked && <span className="label-cap text-[9px] text-secondary">COMING ONLINE</span>}
                {room.locked && <span className="label-cap text-[9px]">SCL-{room.scl}</span>}
              </div>
              <h3 className="font-head font-bold">{room.name}</h3>
              <div className="label-cap mb-1">{room.npc}</div>
              <p className="text-sm text-muted-foreground leading-snug">{room.desc}</p>
            </button>
          );
        })}
      </div>

      <div className="mt-8 panel p-5">
        <div className="label-cap mb-2">The Planet Below — Undiscovered</div>
        <div className="flex flex-wrap gap-2">
          {data.planet.map((p) => (
            <span key={p.id} className="px-3 py-1.5 rounded-[var(--radius)] bg-muted text-muted-foreground text-xs mono opacity-60">{p.name} · LOCKED</span>
          ))}
        </div>
      </div>
    </div>
  );
}
